import * as React from 'react';

import Chip, { ChipProps } from '@mui/material/Chip';

import { ShippingStatus } from '../../graphql/API';

type StatusLabelProps = {
  type: StatusType;
  status?: string | null;
  emptyBackground?: boolean;
  fontWeight?: number;
};

type StatusType = 'shippingStatus' | 'accessRequestStatus' | 'promotionStatus';

type StatusMapResult = {
  colorCode: ChipProps['color'] | string;
  text: string;
  border?: boolean;
  borderColor?: string;
};

type StatusMapDict = Record<string, StatusMapResult>;
type StatusMap = Record<string, StatusMapDict>;

type ColorResult = { color: string; backgroundColor: string };

type ColorMap = Record<Exclude<ChipProps['color'] | string, undefined>, ColorResult>;

type ChipStatusColor = Omit<StatusMapResult, 'colorCode'> & { colorMap: ColorResult };

const colorMap: ColorMap = {
  default: { color: 'black', backgroundColor: '' },
  primary: { color: '', backgroundColor: '#000' },
  secondary: { color: '#848484', backgroundColor: '#F6F6F6' },
  success: { color: '#37BB90', backgroundColor: 'rgba(55, 187, 144, 0.10)' },
  warning: { color: '#955CCC', backgroundColor: '' },
  error: { color: '#DC5564', backgroundColor: 'rgba(233, 63, 63, 0.10)' },
  info: { color: '#BD942A', backgroundColor: '#DBAF3E1A' },
  lightBlue: { color: '#486475', backgroundColor: '#D8EBFB' },
};

const statusMap: StatusMap = {
  shippingStatus: {
    [ShippingStatus.waiting]: {
      colorCode: 'info',
      text: 'Waiting for shipment',
      border: true,
      borderColor: 'rgba(219, 175, 62, 0.60)',
    },
    [ShippingStatus.shipped]: {
      colorCode: 'lightBlue',
      text: 'In Transit',
      border: true,
      borderColor: 'rgba(72, 100, 117, 0.60)',
    },
    [ShippingStatus.cancelled]: { colorCode: 'secondary', text: 'Cancelled', border: true },
    ['in-transit']: {
      colorCode: 'lightBlue',
      text: 'In transit',
      border: true,
      borderColor: 'rgba(72, 100, 117, 0.60)',
    },
    delivered: {
      colorCode: 'success',
      text: 'Delivered',
      border: true,
      borderColor: 'rgba(55, 187, 144, 0.60)',
    },
  },
  accessRequestStatus: {
    accepted: { colorCode: 'success', text: 'Accepted' },
    declined: { colorCode: 'error', text: 'Rejected' },
    archived: { colorCode: 'success', text: 'Accepted' },
    expired: { colorCode: 'error', text: 'Expired' },
  },
  promotionStatus: {
    completed: {
      colorCode: 'success',
      text: 'Completed',
      border: true,
      borderColor: 'rgba(55, 187, 144, 0.60)',
    },
    notCompleted: {
      colorCode: 'error',
      text: 'Not completed',
      border: true,
      borderColor: 'rgba(233, 63, 63, 0.60)',
    },
  },
};

const getDefault = (status: StatusType, value: string | undefined | null): ChipStatusColor => {
  if (!value) {
    return { colorMap: colorMap['secondary'], text: 'NA' };
  }

  const { colorCode, ...restProps } = statusMap[status][value] ?? {
    color: 'secondary',
    text: value,
  };

  return { colorMap: colorMap[colorCode ?? 'secondary'], ...restProps };
};

export const StatusLabelV2: React.FC<StatusLabelProps> = ({
  type,
  status,
  emptyBackground,
  fontWeight = 500,
}) => {
  const { colorMap, text, border, borderColor } = React.useMemo(
    () => getDefault(type, status),
    [type, status],
  );

  return (
    status && (
      <Chip
        size='small'
        label={text}
        variant={border ? 'outlined' : 'filled'}
        sx={{
          color: colorMap.color,
          backgroundColor: colorMap.backgroundColor ? colorMap.backgroundColor : 'undefined',
          borderRadius: 0.6,
          fontWeight,
          fontSize: '14px',
          border: border ? `1px solid ${borderColor}` : undefined,
          ...(emptyBackground
            ? {
                backgroundColor: 'unset',
                '& .MuiChip-label': {
                  padding: 0,
                },
              }
            : undefined),
        }}
      />
    )
  );
};
